import React, { useEffect } from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "./useAuth";
import { SUBSCRIPTION_TYPE } from "../utils/subscriptionTypes";
import { Role } from "../constant/RoleConstants";
import Loader from "../utils/Loading";

const PREMIUM_ROUTES = [
  "/analytics",
  "/content-creation",
  "/posting-publishing",
  "/campaigns",
];

// Routes that require subscription checking
const SUBSCRIPTION_REQUIRED_ROUTES = [
  "/analytics",
  "/content-creation",
  "/posting-publishing",
  "/campaigns",
  // Add hiring manager routes
  "/hiring-manager/analytics",
  "/hiring-manager/content-creation",
  "/hiring-manager/posting-publishing",
  "/hiring-manager/campaigns",
];

interface ProtectedComponentProps {
  isAllow: boolean;
  children?: React.ReactNode;
}

const PrivateComponent: React.FC<ProtectedComponentProps> = ({
  isAllow,
  children,
}) => {
  const location = useLocation();
  const { user } = useAuth();

  // Helper function to determine redirect path based on subscription
  const getSubscriptionRedirectPath = (subscription: any) => {
    if (!subscription) {
      return "/subscriptions";
    }
    
    // If user has active subscription but it's BASIC, redirect to update page
    if (subscription.status === "ACTIVE" && subscription.type === SUBSCRIPTION_TYPE.BASIC) {
      return "/update-subscriptions";
    }
    
    // For all other cases (FREE, EXPIRED, etc.), redirect to subscriptions page
    return "/subscriptions";
  };

  // Check if we're on subscription-related pages to prevent redirect loops
  const isOnSubscriptionPage =
    location.pathname === "/subscriptions" ||
    location.pathname === "/update-subscriptions" ||
    location.pathname === "/subscriptionplan" ||
    location.pathname.includes("/payment") ||
    location.pathname.startsWith("/hiring-manager/subscriptions") ||
    location.pathname.startsWith("/hiring-manager/update-subscriptions");

  // Check if current route requires subscription verification
  const requiresSubscriptionCheck = SUBSCRIPTION_REQUIRED_ROUTES.some((route) =>
    location.pathname.startsWith(route)
  );

  // Use the main subscription context
  const { subscription, isLoading, hasActiveSubscription, checkSubscription } =
    useSubscription();

  // Trigger subscription check whenever user hits a subscription-required route
  useEffect(() => {
    if (requiresSubscriptionCheck && isAllow) {
      checkSubscription();
    }
  }, [
    location.pathname,
    requiresSubscriptionCheck,
    isAllow,
    checkSubscription,
  ]);

  // If user is not authenticated, redirect to login
  if (!isAllow) {
    return <Navigate to="/login" replace />;
  }

  if (user?.isEmailVerify === false) {
    return (
      <Navigate
        to="/emailverify"
        state={{ email: user.email, resendOtp: true }}
        replace
      />
    );
  }

  // Hiring managers are not gated by subscription — company access is via recruiter.
  if (user?.status === Role.HIRING_MANAGER) {
    return children ? <>{children}</> : <Outlet />;
  }

  // If we're on subscription pages, allow access regardless of subscription status
  if (isOnSubscriptionPage) {
    return children ? <>{children}</> : <Outlet />;
  }

  // If this route requires subscription check
  if (requiresSubscriptionCheck) {
    // If subscription data is still loading, show a loader
    if (isLoading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        ></div>
      );
    }

    // If subscription is unknown (null), trigger a check and block with loader
    if (subscription === null) {
      checkSubscription();
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Loader />
        </div>
      );
    }

    if (!hasActiveSubscription) {
      const redirectPath = getSubscriptionRedirectPath(subscription);
      return <Navigate to={redirectPath} replace />;
    }

    // Check if the current route is a premium route
    const isPremiumRoute = PREMIUM_ROUTES.some(
      (route) =>
        location.pathname.startsWith(route) ||
        location.pathname.startsWith(`/hiring-manager${route}`)
    );

    // If it's a premium route and the user cannot access it, redirect to appropriate subscription page
    if (isPremiumRoute && !subscription?.isPremium) {
      const redirectPath = getSubscriptionRedirectPath(subscription);
      return <Navigate to={redirectPath} replace />;
    }
  }

  // Allow access to the route if authenticated and subscription checks pass
  return children ? <>{children}</> : <Outlet />;
};

export default PrivateComponent;
